import { Component } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LucideAngularModule, Upload, Download, X, FileUp } from 'lucide-angular';
import { CsvPreviewComponent, CsvRecord } from './components/csv-preview.component';

@Component({
  selector: 'app-automated-tests-page',
  standalone: true,
  imports: [
    FormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatSnackBarModule,
    MatTooltipModule,
    LucideAngularModule,
    CsvPreviewComponent
],
  templateUrl: './automated-tests-page.component.html',
  styleUrls: ['./automated-tests-page.component.scss']
})
export class AutomatedTestsPageComponent {
  csvRecords: CsvRecord[] = [];
  fileName: string = '';
  showPreview: boolean = false;
  private csvSeparator: string = ';';
  private originalFile: File | null = null;

  columnDescriptions = [
    { key: 'id_conversacion', value: 'Identificador único de la conversación' },
    { key: 'conversacion_modelo', value: 'Texto de la conversación que se enviará al modelo' },
    { key: 'descripcion_resultado_esperado', value: 'Descripción del resultado que se espera obtener' },
    { key: 'agente', value: 'Agente asignado para la conversación' },
    { key: 'enviar_a', value: 'Destinatario o canal donde enviar el resultado' }
  ];

  constructor(
    private snackBar: MatSnackBar
  ) {}

  // Lucide icons
  readonly Upload = Upload;
  readonly Download = Download;
  readonly X = X;
  readonly FileUp = FileUp;

  onFileSelect(event: any): void {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      this.fileName = file.name;
      this.originalFile = file; // Guardar el archivo original
      this.parseCSV(file);
    } else {
      this.snackBar.open('Por favor seleccione un archivo CSV válido.', '', {
        duration: 4000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
    }
  }

  private parseCSV(file: File): void {
    const reader = new FileReader();
    reader.onload = (e) => {
      const csv = e.target?.result as string;
      
      if (!csv || csv.trim().length === 0) {
        this.snackBar.open('El archivo CSV está vacío.', '', {
          duration: 4000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        return;
      }

      // Detectar el separador (punto y coma o coma) desde la primera línea
      const firstLineEnd = csv.indexOf('\n');
      const firstLine = firstLineEnd > -1 ? csv.substring(0, firstLineEnd) : csv;
      const separator = firstLine.includes(';') ? ';' : ',';
      this.csvSeparator = separator;
      
      // Parsear el CSV completo manejando saltos de línea dentro de comillas
      const records = this.parseCSVContent(csv, separator);

      // Validar headers
      const headers = records[0];
      const expectedColumns = ['id_conversacion', 'conversacion_modelo', 'descripcion_resultado_esperado', 'agente', 'enviar_a'];
      const hasRequiredColumns = expectedColumns.every(col => 
        headers.some(header => header.toLowerCase().includes(col.toLowerCase()))
      );

      if (!hasRequiredColumns) {
        const columnsText = expectedColumns.map(col => `- ${col}`).join('\n');
        this.snackBar.open(`El archivo CSV debe contener las columnas:\n\n${columnsText}`, '', {
          duration: 6000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        return;
      }
      
      this.csvRecords = [];
      
      // Procesar los datos (saltar el header)
      for (let i = 1; i < records.length; i++) {
        const values = records[i];
        if (values.length >= 5) {
          const record: CsvRecord = {
            id_conversacion: values[0] || '',
            conversacion_modelo: values[1] || '',
            descripcion_resultado_esperado: values[2] || '',
            agente: values[3] || '',
            ejecutar: 'no', // Valor predeterminado desactivado
            enviar_a: values[4] || ''
          };
          this.csvRecords.push(record);
        }
      }
      
      this.showPreview = true;
    };
    reader.readAsText(file, 'UTF-8');
  }

  private parseCSVContent(csv: string, separator: string): string[][] {
    const records: string[][] = [];
    const state = {
      currentRecord: [] as string[],
      currentField: '',
      inQuotes: false,
      position: 0,
      csv: csv
    };

    while (state.position < csv.length) {
      const char = csv[state.position];
      const nextChar = csv[state.position + 1];
      
      const action = this.determineAction(char, nextChar, separator, state.inQuotes);
      this.executeAction(action, state, records, char);
    }

    this.finalizeLastRecord(state, records);
    return records;
  }

  private determineAction(char: string, nextChar: string, separator: string, inQuotes: boolean): string {
    if (this.isEscapedQuote(char, nextChar, inQuotes)) return 'ESCAPED_QUOTE';
    if (this.isQuote(char)) return 'QUOTE';
    if (this.isSeparator(char, separator, inQuotes)) return 'SEPARATOR';
    if (this.isLineEnd(char, inQuotes)) return 'LINE_END';
    if (this.isCarriageReturn(char)) return 'IGNORE';
    return 'APPEND';
  }

  private executeAction(action: string, state: any, records: string[][], char: string): void {
    switch (action) {
      case 'ESCAPED_QUOTE':
        state.currentField += '"';
        state.position += 2;
        break;
      case 'QUOTE':
        state.inQuotes = !state.inQuotes;
        state.position++;
        break;
      case 'SEPARATOR':
        this.addFieldToRecord(state);
        state.position++;
        break;
      case 'LINE_END':
        this.finishRecord(state, records);
        state.position += this.getLineEndSkip(char, state.csv[state.position + 1]);
        break;
      case 'APPEND':
        state.currentField += char;
        state.position++;
        break;
      default:
        state.position++;
    }
  }

  private isEscapedQuote(char: string, nextChar: string, inQuotes: boolean): boolean {
    return char === '"' && inQuotes && nextChar === '"';
  }

  private isQuote(char: string): boolean {
    return char === '"';
  }

  private isSeparator(char: string, separator: string, inQuotes: boolean): boolean {
    return char === separator && !inQuotes;
  }

  private isLineEnd(char: string, inQuotes: boolean): boolean {
    return (char === '\n' || char === '\r') && !inQuotes;
  }

  private isCarriageReturn(char: string): boolean {
    return char === '\r';
  }

  private addFieldToRecord(state: any): void {
    state.currentRecord.push(state.currentField.trim());
    state.currentField = '';
  }

  private finishRecord(state: any, records: string[][]): void {
    if (state.currentField.trim() || state.currentRecord.length > 0) {
      state.currentRecord.push(state.currentField.trim());
      if (state.currentRecord.some((field: string) => field.length > 0)) {
        records.push([...state.currentRecord]);
      }
      state.currentRecord = [];
      state.currentField = '';
    }
  }

  private getLineEndSkip(char: string, nextChar: string): number {
    return (char === '\r' && nextChar === '\n') ? 2 : 1;
  }

  private finalizeLastRecord(state: any, records: string[][]): void {
    if (state.currentField.trim() || state.currentRecord.length > 0) {
      state.currentRecord.push(state.currentField.trim());
      if (state.currentRecord.some((field: string) => field.length > 0)) {
        records.push(state.currentRecord);
      }
    }
  }

  downloadTemplate(): void {
    const csvContent = [
      'id_conversacion;conversacion_modelo;descripcion_resultado_esperado;agente;enviar_a',
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'plantilla_pruebas_automatizadas.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  clearPreview(): void {
    this.showPreview = false;
    this.csvRecords = [];
    this.fileName = '';
    this.csvSeparator = ';'; // Reset al separador por defecto
    this.originalFile = null; // Limpiar archivo original
    // Reset file input
    const fileInput = document.getElementById('file-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }
}
